import { z } from 'zod'
import { apiFetch } from './apiFetch'
import { isDevMock } from '../dev/isDevMock'
import { securityNonceUrl } from '../guidance/polish/security/nonce-url'
import { DEFAULT_SECURITY_NONCE_PATH } from '../guidance/polish/security/constants'
import type { NonceCheckResult } from '../../netlify/functions/lib/security-nonce-check'

// Client wrapper around the verify-security-nonce endpoint (see
// netlify/functions/verify-security-nonce.ts). Both halves must run server-side: the nonce is minted
// with server entropy, and a browser cannot read the cross-origin body of the user's own site.

export type { NonceCheckResult }

/** Where the nonce has to be readable, computed locally so the instructions survive a remount and are
 *  on screen before the mint returns. The server derives the same URL from the same origin. */
export function securityNoncePublishUrl(targetUrl: string): string {
  return securityNonceUrl(targetUrl) || DEFAULT_SECURITY_NONCE_PATH
}

/**
 * A paste-ready instruction for the user's own coding agent.
 *
 * Publishing this file is a change to THEIR codebase, which their agent can make and ours cannot. So
 * rather than explaining the task to a person who then re-explains it to Cursor or Claude Code, hand
 * over the finished brief: the exact URL and value, the response contract, and the two rewrite/auth
 * traps that produce a 200 with the wrong body. Framework-neutral on purpose — their agent can see the
 * repo and we cannot, so it picks the idiomatic place.
 */
export function buildSecurityAgentPrompt(targetUrl: string, nonce: string): string {
  const url = securityNoncePublishUrl(targetUrl)
  return [
    'Add a site-ownership file to this app so Replay QA can verify I control it before running security tests.',
    '',
    'Requirements:',
    `- URL: ${url}`,
    '- Must return HTTP 200 with this exact value as the response body, as plain text (no HTML wrapper, no quotes, no JSON):',
    `  ${nonce}`,
    '- Must be reachable without authentication, and must not redirect.',
    '- Must be served from this exact origin and path — the path is fixed and cannot be changed.',
    '',
    'Watch out for:',
    '- A catch-all or SPA rewrite that serves index.html for unknown paths. Exclude this path so the raw text is returned.',
    '- Auth middleware, or bot/DDoS protection, sitting in front of all routes. Allowlist this path.',
    '- Build configs or .gitignore rules that drop dot-directories, which can silently omit .well-known from the deployed output.',
    '',
    'Use whatever is idiomatic here: a static file for a framework with a public directory, or a route handler otherwise. Then tell me what you changed and what I need to deploy.',
  ].join('\n')
}

export const nonceCheckResultSchema = z.object({
  url: z.string(), verified: z.boolean(),
  reason: z.enum(['ok', 'mismatch', 'not-found', 'redirected', 'unreachable', 'private-host', 'invalid-url', 'self-origin', 'waived']),
  statusCode: z.number().optional(), responseUrl: z.string().optional(), responseSha256: z.string().optional(), responseLength: z.number().optional(),
})

/** Fresh verification mutation: never reuse a cached ownership result to authorize a probe. */
export async function verifySecurityNonce(targetUrl: string, nonce: string, projectId?: string): Promise<NonceCheckResult> {
  if (isDevMock()) return mockVerify(targetUrl, nonce)
  const res = await apiFetch('/api/verify-security-nonce', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(projectId ? { projectId } : { url: targetUrl, nonce }),
  })
  if (!res.ok) throw new Error(await errorText(res, 'Could not check the file'))
  return nonceCheckResultSchema.parse(await res.json())
}

/** A sentence for each failure the check can report, in the user's terms rather than the protocol's. */
export function nonceFailureMessage(result: NonceCheckResult): string {
  switch (result.reason) {
    case 'not-found':
      return `Nothing is published at that path yet${result.statusCode ? ` (${result.statusCode})` : ''}. Add the file, deploy, then check again.`
    case 'mismatch':
      return 'The file is there, but it does not contain this project\'s value. Copy the value again exactly, with nothing else on the line.'
    case 'redirected':
      return 'That path redirects somewhere else, so we cannot read it. Serve the file directly — many hosts need static or well-known paths excluded from auth and redirects.'
    case 'private-host':
      return 'This app is not reachable from the public internet, so ownership cannot be checked this way. Security probing needs a publicly reachable URL.'
    case 'self-origin':
      return 'That URL points back at Replay QA itself rather than your app. Enter the public URL of the site you want to test.'
    case 'invalid-url':
      return 'That does not look like a URL we can check.'
    default:
      return 'Could not reach the site. Check it is up and try again.'
  }
}

// Dev-mock verification: a URL containing "verified" passes, everything else reports not-found, so both
// branches of the card are reachable without a backend.
function mockVerify(targetUrl: string, _nonce: string): NonceCheckResult {
  const verified = /verified/i.test(targetUrl)
  return {
    url: securityNonceUrl(targetUrl) || securityNonceUrl('https://example.com'),
    verified,
    reason: verified ? 'ok' : 'not-found',
    statusCode: verified ? 200 : 404,
  }
}

async function errorText(res: Response, fallback: string): Promise<string> {
  try {
    const detail = ((await res.json()) as { error?: string }).error
    if (detail) return detail
  } catch { /* non-JSON */ }
  return `${fallback} (${res.status})`
}
